import { createContext, useCallback, useMemo, type ReactNode } from "react";

import { cloneDeep, compact, uniq } from "lodash";
import { useEffectOnce, useLocalStorage } from "usehooks-ts";
import { v4 as uuidv4 } from "uuid";

import { useUser } from "@components/user-context";
import { useQuickFilters } from "@features/quick-filters";
import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import { useUniversalBenchmarksMetrics } from "@features/universal-benchmarks";
import { type UniversalBenchmarksStage } from "@features/universal-benchmarks/consumer/types";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import {
  todayLocalAsISODate,
  type QuickFilter,
  type QuickFilterRevenueConfig,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import {
  FALLBACK_QUICK_FILTER,
  STACKED_CHART_SUPPORTED_METRICS,
} from "../constants";
import {
  makeAdKeyDimension,
  makeDateDimension,
  makeNameDimension,
  makeStatusDimension,
} from "../dimensions";
import { type SalesV4ContextDescriptor } from "../types";
import { mapBenchmarkMetric } from "../utils";

import { useChartStateQueryParams } from "./hooks/use-chart-state";
import { useCompareDateRangeQueryParams } from "./hooks/use-compare-date-range-query-params";
import { useDateRangeQueryParams } from "./hooks/use-date-range-query-params";
import { useMetrics } from "./hooks/use-metrics";
import { useQuickFiltersQueryParams } from "./hooks/use-quick-filters-query-params";
import { useSelectedBreakdownsQueryParams } from "./hooks/use-selected-breakdowns-query-params";
import { useTableStatesQueryParams } from "./hooks/use-table-states-query-params";
import { useTimeGranularityQueryParams } from "./hooks/use-time-granularity-query-params";

export const SalesV4Context = createContext<
  SalesV4ContextDescriptor | undefined
>(undefined);

export const SalesV4Provider = ({ children }: { children: ReactNode }) => {
  const { user } = useUser();
  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    setAccountingMode,
    setAttributionWindow,
  } = useUserAttributionSettings();
  const { metricsV2 } = useUniversalBenchmarksMetrics({
    attributionModel,
    attributionWindow,
    accountingMode,
  });
  const { quickFilters } = useQuickFilters();

  const { dateRange, setDateRange } = useDateRangeQueryParams();
  const { compareDateRange, setCompareDateRange } =
    useCompareDateRangeQueryParams();
  const { timeGranularity, setTimeGranularity } =
    useTimeGranularityQueryParams();
  const { tableStates, setTableStates } = useTableStatesQueryParams();
  const { chartState, setChartState } = useChartStateQueryParams();
  const { quickFilterId, setQuickFilterId } = useQuickFiltersQueryParams();
  const { selectedBreakdownIds, setSelectedBreakdownIds } =
    useSelectedBreakdownsQueryParams();

  const [showPercentChange, setShowPercentChange] = useLocalStorage(
    "sales-v4-show-percent-change",
    false,
  );
  const [universalBenchmarksStage, setUniversalBenchmarksStage] =
    useLocalStorage<UniversalBenchmarksStage>(
      "sales-v4-universal-benchmarks-stage",
      "blended",
    );

  const dateRangeInterval = useMemo(
    () => parseDateRangeArgument(dateRange, todayLocalAsISODate()),
    [dateRange],
  );

  const compareDateRangeInterval = useMemo(() => {
    return deriveComparisonDateRange(
      dateRangeInterval,
      compareDateRange,
      user.timezone,
      timeGranularity,
    );
  }, [dateRangeInterval, compareDateRange, timeGranularity, user.timezone]);

  const { metrics } = useMetrics(timeGranularity);

  const dimensions = useMemo(() => {
    return [
      makeDateDimension(),
      makeNameDimension(),
      makeAdKeyDimension(),
      makeStatusDimension(),
    ];
  }, []);

  const applyQuickFilter = useCallback(
    (filter: QuickFilter<QuickFilterRevenueConfig>) => {
      setQuickFilterId(filter.id);

      setDateRange(filter.config.dateRange);
      setCompareDateRange(filter.config.compareDateRange);
      setTimeGranularity(filter.config.timeGranularity ?? "daily");
      setSelectedBreakdownIds([...filter.config.breakdownIds]);

      const metricsV2Ids = metricsV2.map((e) => e.id);

      setTableStates([
        {
          showPercentChange,
          selectedTab: filter.config.selectedTab,
          searchText: "",
          selectedBreakdownId: filter.config.breakdownIds[0],
          selectedMetricIds: uniq(
            compact(
              filter.config.tableMetricIds.map((e) =>
                mapBenchmarkMetric(metricsV2Ids, e),
              ),
            ),
          ),
          metricFilters: [],
          breakdownFilters: cloneDeep(filter.config.breakdownFilters),
          sorting: cloneDeep(filter.config.sorting),
          breakdownSelection: [],
          campaignSelection: [],
          adsetSelection: [],
          adSelection: [],
          pageIndex: 0,
          pageSize: 25,
        },
      ]);

      setChartState({
        metrics: filter.config.chartMetricIds.map((metricId, index) => {
          return {
            id: uuidv4(),
            name: null,
            metricId,
            accountingMode: null,
            attributionModel: null,
            attributionWindow: null,
            compareDateRange: null,
            filterQuery: null,
            filters: cloneDeep(filter.config.breakdownFilters),
            advancedSearch: null,
            active: false,
            visible: true,
            index,
          };
        }),
        alignAxesScale: filter.config.alignAxesScale,
        displayMode: STACKED_CHART_SUPPORTED_METRICS.includes(
          filter.config.chartMetricIds[0],
        )
          ? "stacked"
          : "split",
      });

      if (accountingMode !== filter.config.accountingMode) {
        setAccountingMode(filter.config.accountingMode);
        setAttributionWindow(
          filter.config.accountingMode === "cash" ? "infinity" : "1",
        );
      }
    },
    [
      metricsV2,
      accountingMode,
      setQuickFilterId,
      setDateRange,
      setCompareDateRange,
      setTimeGranularity,
      setSelectedBreakdownIds,
      setTableStates,
      setChartState,
      setAccountingMode,
      setAttributionWindow,
      showPercentChange,
    ],
  );

  const setShowPercentChangeOnTable = useCallback(
    (val: boolean) => {
      setShowPercentChange(val);
      setTableStates(
        tableStates.map((e) => ({ ...e, showPercentChange: val })),
      );
    },
    [setShowPercentChange, setTableStates, tableStates],
  );

  const selectedQuickFilter = useMemo(() => {
    return (
      quickFilters.find((e) => e.id === quickFilterId) ||
      quickFilters[0] ||
      FALLBACK_QUICK_FILTER
    );
  }, [quickFilters, quickFilterId]);

  useEffectOnce(() => {
    const quickFilter = quickFilters.find((e) => e.id === quickFilterId);

    if (!quickFilter && quickFilters[0]) {
      setTimeout(() => {
        applyQuickFilter(quickFilters[0]);
      }, 10);
    }
  });

  return (
    <SalesV4Context.Provider
      value={{
        dateRange,
        compareDateRange,
        dateRangeInterval,
        compareDateRangeInterval,
        timeGranularity,
        tableStates,
        selectedBreakdownIds,
        dimensions,
        metrics,
        selectedQuickFilter,
        chartState,
        universalBenchmarksStage,
        setDateRange,
        setCompareDateRange,
        setTimeGranularity,
        setTableStates,
        setSelectedBreakdownIds,
        setChartState,
        setShowPercentChangeOnTable,
        setUniversalBenchmarksStage,
        applyQuickFilter,
      }}
    >
      {children}
    </SalesV4Context.Provider>
  );
};
